import React from 'react';
import Icon from '../../../components/AppIcon';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const PortfolioOverview = ({ portfolioData, performanceData }) => {
  const { totalValue, dailyChange, weeklyChange, monthlyChange } = portfolioData;
  
  const allocationData = [
    { name: 'Bitcoin', value: 45.2, amount: 156420.50, color: '#F7931A' },
    { name: 'Ethereum', value: 28.7, amount: 99340.20, color: '#627EEA' },
    { name: 'Cardano', value: 12.1, amount: 41890.30, color: '#0033AD' },
    { name: 'Solana', value: 8.5, amount: 29450.80, color: '#9945FF' },
    { name: 'Others', value: 5.5, amount: 19048.70, color: '#6B7280' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(value);
  };

  const formatPercentage = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value?.toFixed(2)}%`;
  };

  const getPerformanceColor = (value) => {
    if (value > 0) return 'text-success';
    if (value < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-md p-3 shadow-elevation-2">
          <p className="font-medium text-popover-foreground">{data?.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(data?.amount)} ({data?.value}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-card-foreground">Portfolio Overview</h2>
        <div className="flex items-center space-x-2">
          <Icon name="TrendingUp" size={20} className="text-success" />
          <span className="text-sm text-muted-foreground">Last updated: 2 min ago</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Portfolio Value & Performance */}
        <div className="space-y-6">
          <div className="text-center lg:text-left">
            <p className="text-sm text-muted-foreground mb-2">Total Portfolio Value</p>
            <h3 className="text-3xl font-bold text-card-foreground mb-4">
              {formatCurrency(totalValue)}
            </h3>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-md">
              <p className="text-xs text-muted-foreground mb-1">24H Change</p>
              <p className={`text-sm font-medium ${getPerformanceColor(dailyChange)}`}>
                {formatPercentage(dailyChange)}
              </p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-md">
              <p className="text-xs text-muted-foreground mb-1">7D Change</p>
              <p className={`text-sm font-medium ${getPerformanceColor(weeklyChange)}`}>
                {formatPercentage(weeklyChange)}
              </p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-md">
              <p className="text-xs text-muted-foreground mb-1">30D Change</p>
              <p className={`text-sm font-medium ${getPerformanceColor(monthlyChange)}`}>
                {formatPercentage(monthlyChange)}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-card-foreground">Top Holdings</h4>
            {allocationData?.slice(0, 3)?.map((item, index) => (
              <div key={item?.name} className="flex items-center justify-between p-3 bg-muted/30 rounded-md">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item?.color }}
                  />
                  <span className="text-sm font-medium text-card-foreground">{item?.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-card-foreground">
                    {formatCurrency(item?.amount)}
                  </p>
                  <p className="text-xs text-muted-foreground">{item?.value}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Allocation Pie Chart */}
        <div className="flex flex-col">
          <h4 className="text-sm font-medium text-card-foreground mb-4">Asset Allocation</h4>
          <div className="flex-1 min-h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={allocationData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {allocationData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value, entry) => (
                    <span className="text-xs text-card-foreground">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioOverview;